import React, { useContext } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { AuthContext } from '../App';
import { Button } from './ui/button';

export default function Navigation() {
  const { user, logout } = useContext(AuthContext);
  const location = useLocation();

  const isActive = (path) => location.pathname === path;

  const menuItems = [
    {
      path: '/',
      label: 'Dashboard',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
        </svg>
      )
    },
    {
      path: '/contracts',
      label: 'Contrats',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      )
    },
    {
      path: '/suppliers',
      label: 'Fournisseurs',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-4m-5 0H3m2 0h3M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
        </svg>
      )
    },
    {
      path: '/users',
      label: 'Utilisateurs',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
        </svg>
      ),
      adminOnly: true
    }
  ];

  const getRoleInfo = (role) => {
    switch (role) {
      case 'admin':
        return { label: 'Admin', color: 'text-error' };
      case 'gestionnaire':
        return { label: 'Manager', color: 'text-primary' };
      default:
        return { label: 'User', color: 'text-success' };
    }
  };

  const roleInfo = getRoleInfo(user?.role);

  return (
    <div className="nav-sidebar h-full flex flex-col" data-testid="navigation-sidebar">
      {/* Header */}
      <div className="p-6 border-b border-border-light bg-bg-subtle">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-gradient-to-br from-primary to-secondary rounded-lg flex items-center justify-center">
            <span className="text-white font-bold text-sm">DSI</span>
          </div>
          <div>
            <h2 className="font-bold text-text-primary">Gestion de contrats</h2>
            <p className="text-xs text-text-muted font-mono">v2.1.0</p>
          </div>
        </div>
      </div>

      {/* Navigation Menu */}
      <nav className="flex-1 p-4">
        <div className="text-xs font-semibold text-text-muted mb-3 uppercase tracking-wide">Navigation</div>
        
        <ul className="space-y-1">
          {menuItems.map((item) => {
            // Hide admin-only items for non-admin users
            if (item.adminOnly && user?.role !== 'admin') {
              return null;
            }
            
            return (
              <li key={item.path}>
                <Link
                  to={item.path}
                  data-testid={`nav-link-${item.label.toLowerCase()}`}
                  className={`nav-link ${isActive(item.path) ? 'active' : ''}`}
                >
                  {item.icon}
                  <span className="font-medium">{item.label}</span>
                </Link>
              </li>
            );
          })}
        </ul>

        {/* Quick Actions */}
        <div className="mt-8 pt-6 border-t border-border-light">
          <div className="text-xs font-semibold text-text-muted mb-3 uppercase tracking-wide">Actions</div>
          <Link to="/contracts/new">
            <Button 
              variant="outline" 
              size="sm" 
              className="w-full justify-start btn-outline"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              Nouveau contrat
            </Button>
          </Link>
        </div>
      </nav>

      {/* User Info & Session */}
      <div className="p-4 border-t border-border-light bg-bg-subtle">
        <div className="mb-4">
          <div className="flex items-center justify-between mb-1">
            <span className="font-semibold text-sm text-text-primary" data-testid="user-name">
              {user?.full_name}
            </span>
            <span className={`text-xs px-2 py-1 rounded-full bg-opacity-20 ${roleInfo.color}`} data-testid="user-role">
              {roleInfo.label}
            </span>
          </div>
          <div className="text-xs text-text-muted" data-testid="user-email">
            {user?.email}
          </div>
        </div>

        <Button
          onClick={logout}
          size="sm"
          className="w-full btn-danger justify-center"
          data-testid="logout-button"
        >
          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
          </svg>
          Déconnexion
        </Button>
      </div>
    </div>
  );
}